import { lazy } from "react"
import { useSelector } from "react-redux"
import { Routes, Route, Navigate } from "react-router"
import PrivateRoute from "./PrivateRoute"
import Loader from "@/components/Loader"
import Layout from "@/components/shared/Layout"
import { SpinnerSize } from "@/components/shared/Spinner"
import {
   CellDetailOverviewRoutes,
   InmateDetailOverviewRoutes,
   PageRoutes,
   SettingsRoutes,
} from "@/constants/routes"
import useQueryParams from "@/hooks/useQueryParams"
import CellDetailOverview from "@/pages/CellDetailOverview"
import AlertSettings from "@/pages/CellDetailOverview/AlertSettings"
import Export from "@/pages/CellDetailOverview/Export"
import CellOpenCellSchedules from "@/pages/CellDetailOverview/OpenCellSchedules"
import Summary from "@/pages/CellDetailOverview/Summary"
import ControlPanel from "@/pages/ControlPanel"
import EntraCallback from "@/pages/EntraCallback"
import FloorOverview from "@/pages/FloorOverview/FloorOverview"
import InmateDetailOverview from "@/pages/InmateDetailOverview"
import InmateExport from "@/pages/InmateDetailOverview/Export"
import InmateSummary from "@/pages/InmateDetailOverview/Summary"
import Login from "@/pages/Login"
import MFARequired from "@/pages/MFARequired"
import NewPassword from "@/pages/NewPassword"
import PasswordRecovery from "@/pages/PasswordRecovery"
import Preferences from "@/pages/Preferences"
import AlertSetting from "@/pages/Settings/Alerts/AlertSetting"
import WearablesOverview from "@/pages/WearablesOverview"
import { RootState } from "@/store"
import { QueryParameter } from "@/types/index"
import { UserRole } from "@/types/user"
import { isNull } from "@/utils/lodash"
const Demo = lazy(() => import("@/pages/Demo"))
const Settings = lazy(() => import("@/pages/Settings"))
const Locations = lazy(() => import("@/pages/Settings/Locations"))
const Cells = lazy(() => import("@/pages/Settings/Cells"))
const Inmates = lazy(() => import("@/pages/Settings/Inmates"))
const Users = lazy(() => import("@/pages/Settings/Users"))
const FacilityLevels = lazy(() => import("@/pages/Settings/FacilityLevels"))
const Facility = lazy(() => import("@/pages/Settings/Facility"))
const Alerts = lazy(() => import("@/pages/Settings/Alerts"))
const General = lazy(() => import("@/pages/Settings/General"))
const OpenCellSchedules = lazy(
   () => import("@/pages/Settings/OpenCellSchedules")
)

const SETTINGS_PAGES = {
   path: PageRoutes.SETTINGS,
   element: <Settings />,
   children: [
      {
         path: SettingsRoutes.FACILITY_LEVELS,
         element: <FacilityLevels />,
      },
      {
         path: SettingsRoutes.FACILITY,
         element: <Facility />,
      },
      {
         path: SettingsRoutes.LOCATIONS,
         element: <Locations />,
      },
      {
         path: SettingsRoutes.CELLS,
         element: <Cells />,
      },
      {
         path: SettingsRoutes.INMATES,
         element: <Inmates />,
      },
      {
         path: SettingsRoutes.USERS,
         element: <Users />,
      },
      {
         path: SettingsRoutes.ALERTS,
         element: <Alerts />,
      },
      {
         path: SettingsRoutes.GENERAL,
         element: <General />,
      },
      {
         path: SettingsRoutes.OPEN_CELL_SCHEDULES,
         element: <OpenCellSchedules />,
      },
   ],
} as const

const CELL_DETAIL_OVERVIEW_PAGES = {
   path: PageRoutes.CELL_DETAIL_OVERVIEW,
   element: <CellDetailOverview />,
   children: [
      {
         path: CellDetailOverviewRoutes.SUMMARY,
         element: <Summary />,
      },
      {
         path: CellDetailOverviewRoutes.ALERT_SETTINGS,
         element: <AlertSettings />,
      },
      {
         path: CellDetailOverviewRoutes.EXPORT,
         element: <Export />,
      },
      {
         path: CellDetailOverviewRoutes.OPEN_CELL_SCHEDULES,
         element: <CellOpenCellSchedules />,
      },
   ],
}

const INMATE_DETAIL_OVERVIEW_PAGES = {
   path: PageRoutes.INMATE_DETAIL_OVERVIEW,
   element: <InmateDetailOverview />,
   children: [
      {
         path: InmateDetailOverviewRoutes.SUMMARY,
         element: <InmateSummary />,
      },
      {
         path: InmateDetailOverviewRoutes.EXPORT,
         element: <InmateExport />,
      },
   ],
}

export default function AppRoutes() {
   const { isAuthenticated, loadingLogin, mfaRequired } = useSelector(
      ({ authReducer }: RootState) => authReducer
   )
   const { isSuperAdmin, loading: userLoading } = useSelector(
      ({ userReducer }: RootState) => userReducer
   )
   const { getQueryParamByKey } = useQueryParams()
   const activationToken = getQueryParamByKey(QueryParameter.ACTIVATION) || null
   const recoveryToken = getQueryParamByKey(QueryParameter.RECOVERY) || null

   if (
      isAuthenticated &&
      (loadingLogin || userLoading || isNull(isSuperAdmin))
   ) {
      return <Loader size={SpinnerSize.LARGE} />
   }

   return (
      <Routes>
         {/* Public Routes */}
         {!isNull(activationToken) || !isNull(recoveryToken) ? (
            <Route
               path={PageRoutes.NEW_PASSWORD}
               element={<Layout className="layout--fixed-height" />}
            >
               <Route
                  index
                  element={<NewPassword />}
               />
            </Route>
         ) : (
            <Route
               path="*"
               element={<Navigate to={PageRoutes.LOGIN} />}
            />
         )}

         <Route
            path={PageRoutes.MFA_REQUIRED}
            element={
               isAuthenticated ? (
                  isSuperAdmin ? (
                     <Navigate to={PageRoutes.CONTROL_PANEL} />
                  ) : (
                     <Navigate to={PageRoutes.FLOOR_OVERVIEW} />
                  )
               ) : (
                  <Layout className="layout--fixed-height" />
               )
            }
         >
            <Route
               index
               element={<MFARequired />}
            />
         </Route>

         <Route
            path={PageRoutes.LOGIN}
            element={
               isAuthenticated ? (
                  isSuperAdmin ? (
                     <Navigate to={PageRoutes.CONTROL_PANEL} />
                  ) : (
                     <Navigate to={PageRoutes.FLOOR_OVERVIEW} />
                  )
               ) : mfaRequired ? (
                  <Navigate to={PageRoutes.MFA_REQUIRED} />
               ) : (
                  <Layout className="layout--fixed-height" />
               )
            }
         >
            <Route
               index
               element={<Login />}
            />
         </Route>
         <Route
            path={PageRoutes.ENTRA_CALLBACK}
            element={<Layout className="layout--fixed-height" />}
         >
            <Route
               index
               element={<EntraCallback />}
            />
         </Route>
         <Route
            path={PageRoutes.PASSWORD_RECOVERY}
            element={<Layout className="layout--fixed-height" />}
         >
            <Route
               index
               element={<PasswordRecovery />}
            />
         </Route>

         {/* Private Routes */}
         <Route element={<PrivateRoute />}>
            <Route element={<Layout isAuthLayout />}>
               <Route
                  path={PageRoutes.CONTROL_PANEL}
                  element={
                     !isSuperAdmin ? (
                        <Navigate
                           to={PageRoutes.FLOOR_OVERVIEW}
                           replace
                        />
                     ) : (
                        <ControlPanel />
                     )
                  }
               />
               <Route
                  path={PageRoutes.DEMO}
                  element={<Demo />}
               />
               <Route
                  path={PageRoutes.FLOOR_OVERVIEW}
                  element={<FloorOverview />}
               />
               <Route
                  path={PageRoutes.WEARABLES_OVERVIEW}
                  element={<WearablesOverview />}
               />
               <Route
                  path={PageRoutes.PREFERENCES}
                  element={<Preferences />}
               />
            </Route>
         </Route>

         <Route
            element={
               <PrivateRoute
                  allowedRoles={[UserRole.MANAGEMENT, UserRole.ADMIN]}
               />
            }
         >
            <Route element={<Layout isAuthLayout />}>
               <Route {...INMATE_DETAIL_OVERVIEW_PAGES}>
                  <Route
                     index
                     element={
                        isSuperAdmin ? (
                           <Navigate to={PageRoutes.CONTROL_PANEL} />
                        ) : (
                           <Navigate to={PageRoutes.FLOOR_OVERVIEW} />
                        )
                     }
                  />
                  {INMATE_DETAIL_OVERVIEW_PAGES.children.map(
                     ({ path, element }) => (
                        <Route
                           key={path}
                           path={path}
                           element={element}
                        />
                     )
                  )}
               </Route>
            </Route>
         </Route>

         <Route
            element={
               <PrivateRoute
                  allowedRoles={[UserRole.MANAGEMENT, UserRole.ADMIN]}
               />
            }
         >
            <Route element={<Layout isAuthLayout />}>
               <Route {...CELL_DETAIL_OVERVIEW_PAGES}>
                  <Route
                     index
                     element={
                        isSuperAdmin ? (
                           <Navigate to={PageRoutes.CONTROL_PANEL} />
                        ) : (
                           <Navigate to={PageRoutes.FLOOR_OVERVIEW} />
                        )
                     }
                  />
                  {CELL_DETAIL_OVERVIEW_PAGES.children.map(
                     ({ path, element }) => (
                        <Route
                           key={path}
                           path={path}
                           element={element}
                        />
                     )
                  )}
               </Route>
            </Route>
         </Route>

         <Route element={<PrivateRoute allowedRoles={[UserRole.ADMIN]} />}>
            <Route
               element={
                  <Layout
                     isAuthLayout
                     className="layout--fixed-height"
                  />
               }
            >
               <Route {...SETTINGS_PAGES}>
                  <Route
                     index
                     element={
                        <Navigate
                           to={SETTINGS_PAGES.children[0].path}
                           replace
                        />
                     }
                  />
                  {SETTINGS_PAGES.children.map(({ path, element }) => (
                     <Route
                        key={path}
                        path={path}
                        element={element}
                     />
                  ))}
                  <Route
                     path={SettingsRoutes.ALERTS_DETAIL}
                     element={<AlertSetting />}
                  />
               </Route>
            </Route>
         </Route>
      </Routes>
   )
}
